import {
  Avatar, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Stack, Tab, Tabs, TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';
import { formatDate } from '@/utils/format';
import type { MedicalSpecialty } from '@/types/medical';
import { api } from '@/api/client';

/**
 * Clinicians management.
 *
 * Two tabs:
 *   1. Active — already-verified clinicians at the practice
 *   2. Pending verification — clinicians invited or self-registered, queue
 *      for the practice admin to approve/reject (creates ClinicianVerification
 *      with proof attachments per Pillar 8).
 *
 * "Invite" sends a magic-link email. The new clinician completes their
 * profile, attaches proof of license, and waits for the verification queue.
 */

type ClinicianRow = {
  id: number;
  display_name: string;
  email?: string;
  photo: string | null;
  specialties: { id: number; name: string }[];
  active_license_count: number;
  verification_status: 'pending' | 'in_review' | 'approved' | 'rejected' | 'suspended';
  verification_submitted_at: string | null;
  is_listed: boolean;
  trust_score: string | null;
};

type TabKey = 'active' | 'pending';

export function PracticeCliniciansPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'clinicians'],
    queryFn:  practice.clinicians.list as () => Promise<ClinicianRow[]>,
  });
  const [tab, setTab] = useState<TabKey>('active');
  const [inviteOpen, setInviteOpen] = useState(false);

  const buckets = useMemo(() => {
    const all = q.data ?? [];
    return {
      active:  all.filter((c) => c.verification_status === 'approved'),
      pending: all.filter((c) =>
        c.verification_status === 'pending' || c.verification_status === 'in_review'),
    };
  }, [q.data]);

  return (
    <>
      <PageHeader
        title="Clinicians"
        actions={
          <Button variant="contained" startIcon={<PersonAddIcon />}
                  onClick={() => setInviteOpen(true)}>
            Invite clinician
          </Button>
        }
      />

      <DataState query={q}>
        {() => (
          <>
            <Tabs
              value={tab} onChange={(_, v: TabKey) => setTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="active"  label={`Active (${buckets.active.length})`} />
              <Tab value="pending" label={`Pending verification (${buckets.pending.length})`} />
            </Tabs>
            {buckets[tab].length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {tab === 'active'
                  ? 'No active clinicians yet. Invite the first one above.'
                  : 'No clinicians awaiting verification.'}
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {buckets[tab].map((c) => (
                  tab === 'active'
                    ? <ActiveRow key={c.id} clinician={c} />
                    : <VerificationRow key={c.id} clinician={c} />
                ))}
              </Stack>
            )}
          </>
        )}
      </DataState>

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ActiveRow({ clinician }: { clinician: ClinicianRow }) {
  const initials = (clinician.display_name || '?')
    .split(' ').map((p) => p[0] ?? '').join('').slice(0, 2);
  return (
    <Card
      component={RouterLink}
      to={`/practice/clinicians/${clinician.id}`}
      sx={{ textDecoration: 'none', color: 'inherit',
            '&:hover': { borderColor: 'primary.main' } }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar src={clinician.photo ?? undefined}
                  sx={{ bgcolor: 'primary.main' }}>
            {initials}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {clinician.display_name}
              </Typography>
              <VerificationBadge status={clinician.verification_status} />
              {!clinician.is_listed && (
                <Chip size="small" label="unlisted" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap>
              {clinician.specialties.map((s) => s.name).join(' · ') || '—'}
              {' · '}
              {clinician.active_license_count} license(s)
            </Typography>
          </Box>
          {clinician.trust_score && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block' }}>
                trust
              </Typography>
              <Typography variant="h6" className="tabular">
                {Math.round(parseFloat(clinician.trust_score))}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function VerificationRow({ clinician }: { clinician: ClinicianRow }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [rejectOpen, setRejectOpen] = useState(false);

  const approveMut = useApiMutation({
    mutationFn: () => practice.clinicians.verify(clinician.id, 'approved'),
    onSuccess: () => {
      toast.success(`${clinician.display_name} verified.`);
      qc.invalidateQueries({ queryKey: ['practice', 'clinicians'] });
    },
    onError: (e) => toast.error(e.message),
  });
  const rejectMut = useApiMutation({
    mutationFn: (reason: string) =>
      practice.clinicians.verify(clinician.id, 'rejected', reason),
    onSuccess: () => {
      toast.success('Verification rejected.');
      qc.invalidateQueries({ queryKey: ['practice', 'clinicians'] });
      setRejectOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const initials = (clinician.display_name || '?')
    .split(' ').map((p) => p[0] ?? '').join('').slice(0, 2);

  return (
    <>
      <Card sx={{ borderColor: 'warning.main' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={clinician.photo ?? undefined}
                    sx={{ bgcolor: 'warning.main' }}>
              {initials}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {clinician.display_name}
                </Typography>
                <VerificationBadge status={clinician.verification_status} />
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap>
                {clinician.email && `${clinician.email} · `}
                {clinician.active_license_count} license(s)
                {clinician.verification_submitted_at &&
                  ` · submitted ${formatDate(clinician.verification_submitted_at)}`}
              </Typography>
            </Box>
            <Button size="small" component={RouterLink}
                    to={`/practice/clinicians/${clinician.id}`}
                    variant="text">
              Review
            </Button>
            <Button size="small" variant="contained" color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => approveMut.mutate()}
                    disabled={approveMut.isPending}>
              Approve
            </Button>
            <Button size="small" variant="outlined" color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => setRejectOpen(true)}>
              Reject
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={rejectOpen}
        title="Reject verification?"
        description="The clinician is notified and may resubmit. Provide a clear reason so they know what to fix."
        destructive
        reasonRequired
        confirmLabel="Reject"
        onCancel={() => setRejectOpen(false)}
        onConfirm={(reason) => rejectMut.mutateAsync(reason ?? '')}
      />
    </>
  );
}

function VerificationBadge({ status }: { status: ClinicianRow['verification_status'] }) {
  const config = {
    pending:    { label: 'pending', color: 'warning' as const, icon: <HourglassTopIcon /> },
    in_review:  { label: 'in review', color: 'warning' as const, icon: <HourglassTopIcon /> },
    approved:   { label: 'verified', color: 'success' as const, icon: <VerifiedIcon /> },
    rejected:   { label: 'rejected', color: 'error' as const, icon: <CloseIcon /> },
    suspended:  { label: 'suspended', color: 'error' as const, icon: <CloseIcon /> },
  }[status];
  return (
    <Chip size="small"
          icon={config.icon}
          label={config.label}
          color={config.color}
          variant="outlined" />
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [specialtyId, setSpecialtyId] = useState<string>('');

  const specialtiesQ = useApiQuery({
    queryKey: ['specialties'],
    queryFn:  () => api.get<MedicalSpecialty[]>('/api/medical/specialties/'),
    enabled:  open,
  });

  const inviteMut = useApiMutation({
    mutationFn: () => practice.clinicians.invite({
      email: email.trim(),
      display_name: displayName.trim(),
      ...(specialtyId && { specialty_id: parseInt(specialtyId, 10) }),
    }),
    onSuccess: () => {
      toast.success(`Invitation sent to ${email}.`);
      qc.invalidateQueries({ queryKey: ['practice', 'clinicians'] });
      setEmail(''); setDisplayName(''); setSpecialtyId('');
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = email.includes('@') && displayName.trim().length > 1;

  return (
    <Dialog open={open} onClose={() => !inviteMut.isPending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Invite clinician</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus fullWidth label="Email" type="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            fullWidth label="Display name"
            value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Dr Anna Kowalska"
          />
          <TextField
            fullWidth select label="Primary specialty (optional)"
            value={specialtyId} onChange={(e) => setSpecialtyId(e.target.value)}
          >
            <MenuItem value="">(skip — they'll choose)</MenuItem>
            {(specialtiesQ.data ?? [])
              .filter((s) => s.is_active)
              .map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
              ))}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            <Box component="span" sx={{ display: 'flex', gap: 0.5 }}>
              <GroupsIcon fontSize="small" />
              The invitee receives a magic-link email. They complete their
              profile and attach proof of license. They show up in the
              "Pending verification" tab when ready.
            </Box>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={inviteMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || inviteMut.isPending}
                onClick={() => inviteMut.mutate()}>
          Send invite
        </Button>
      </DialogActions>
    </Dialog>
  );
}
